/**
 * The unit detail header's quick stats, exactly as before #1113 (Eric, 2026-10-30: "Put the old
 * icons back exactly"): Battery "NN%", Zap and the charger's status word, Thermometer and "NN°F",
 * separated by green dots, each opening its tab. The battery figure is the server's live_status
 * reading; no reading shows "—", never a made-up 0%.
 */
import { Battery, Thermometer, Zap } from 'lucide-react';
import { legacyIconState } from '@/lib/legacyStatusIcons';
import { BatteryDirectionMark } from '@/components/unit/BatteryDirectionMark';
import { BatteryCriticalTag } from '@/components/unit/BatteryCriticalTag';
import type { Unit } from '@/types';

type QuickStatTab = 'solar' | 'charger' | 'hvac';

export function UnitDetailQuickStats({ unit, onOpenTab }: { unit: Unit; onOpenTab: (tab: QuickStatTab) => void }) {
  if (!unit.systems) return null;
  const { batteryLevel, batteryFill, direction, critical } = legacyIconState(unit.live_status);
  return (
    <div className="hidden lg:flex items-center gap-3">
      <button
        onClick={() => onOpenTab('solar')}
        className="flex items-center gap-1.5 text-sm text-foreground hover:text-[#8FB560] transition-colors cursor-pointer"
      >
        <Battery className="h-4 w-4" />
        <span className="font-semibold">{batteryLevel == null ? '—' : `${batteryLevel}%`}</span>
        <BatteryDirectionMark direction={direction} fill={batteryFill} className="text-[9px]" />
        <BatteryCriticalTag show={critical} />
      </button>
      <span className="text-[#8FB560] font-bold">•</span>
      <button
        onClick={() => onOpenTab('charger')}
        className="flex items-center gap-1.5 text-sm text-foreground capitalize hover:text-[#8FB560] transition-colors cursor-pointer"
      >
        <Zap className="h-4 w-4" />
        <span className="font-semibold">{unit.systems.charger?.status ?? 'unknown'}</span>
      </button>
      <span className="text-[#8FB560] font-bold">•</span>
      <button
        onClick={() => onOpenTab('hvac')}
        className="flex items-center gap-1.5 text-sm text-foreground hover:text-[#8FB560] transition-colors cursor-pointer"
      >
        <Thermometer className="h-4 w-4" />
        <span className="font-semibold">{unit.systems.hvac?.temperature ?? '--'}°F</span>
      </button>
    </div>
  );
}
